import { useState, useRef, useContext, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Box, Typography, Select, MenuItem,
    FormControl, FormControlLabel, Checkbox, Table, TableBody, TableCell, TableHead,
    TableRow, CircularProgress, Divider, Paper, IconButton, Fab, Zoom,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { AuthContext } from '../../utils/AuthContext';
import { getRates } from '../../services/QuotationService';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import AddIcon from '@mui/icons-material/Add';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import CustomSelect2 from '../CustomSelect2';
import NotesDisplay from '../NotesDisplay';
import RatesVersionSelect from '../RatesVersionSelect';
import PercentInput from '../PercentInput';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { BLANK_QUOTE_RATES, BlankQuoteNotice, LeaveBlankPerQuote, applyLeaveBlank, getBlankQuotesForPlan, isBlankQuoteRates, isQuoteBlankForPlan } from './leaveBlank';
import { ManualEditNotice, withFreshRatesFlag } from './manualPremiumEdits';

const INSURER_ID = 'pacific_cross_select';
const BRAND_COLOR = '#1565c0';
const PREMIUM_ROW_COLOR = '#ffcc80';
const SELECTED_PREMIUM_ROW_COLOR = '#ffb74d';

// Each tier is one whole plan - the room type only decides which of the rate provider's
// pre-computed columns is used, it isn't sent back to the server.
const TIERS = [
    { id: 'pacific_cross_select_plus', key: 'plus', label: 'Select Plus', roomTypes: ['Ward', 'Semi-Private', 'Private 2M', 'Private 3M', 'Private 5M'] },
    { id: 'pacific_cross_select_standard', key: 'standard', label: 'Select Standard', roomTypes: ['Ward', 'Semi-Private', 'Private 2M'] },
];

// Maps a room type label to the slug the rate provider uses to build its response keys,
// e.g. tier 'plus' + room type 'Semi-Private' -> 'select_plus_semi_private_with_travel_plus'.
const ROOM_TYPE_SLUGS = {
    'Ward': 'ward',
    'Semi-Private': 'semi_private',
    'Private 2M': 'private_2m',
    'Private 3M': 'private_3m',
    'Private 5M': 'private_5m',
};

function getRateField(tierKey, roomType) {
    return `select_${tierKey}_${ROOM_TYPE_SLUGS[roomType]}_with_travel_plus`;
}

// Every tier × room type column plus the optional benefits — all are kept so switching
// tier / room type / optional benefits needs no refetch
const RATE_FIELDS = [
    ...TIERS.flatMap(t => t.roomTypes.map(room => getRateField(t.key, room))),
    'op_standard', 'op_executive', 'dental', 'personal_accident_coverage_rate',
];

function formatRate(rate) {
    if (rate === null || rate === undefined) return '-';
    if (typeof rate === 'string') return rate;
    if (isNaN(rate)) return 'Unavailable';
    return '₱' + rate.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isRateAvailable(rate) {
    return typeof rate === 'number' && !isNaN(rate);
}

// Same as PacificCrossSelect_Rates' getRenewalPercent
function getRenewalPercent(discount, loading) {
    return (100 - (parseFloat(discount) || 0) + (parseFloat(loading) || 0)) / 100;
}

export default function PacificCrossSelect_Compact_Rates_Dialog({ open, onClose, appConfig, quotes = [], clients = [], quotations = [], onAddToQuotations, editingPlan = null, isStale = false, insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, onRatesVersionChanged }) {
    const { auth } = useContext(AuthContext);

    const config = appConfig?.content?.specifics?.[INSURER_ID] || {};
    const outpatientRates = config['Outpatient Rates'] || {};
    const dentalRates = config['Dental Rates'] || {};
    const coPaymentRates = config['Co-Payment Rates'] || {};
    const personalAccidentCoverageRates = config['Personal Accident Coverage Rates'] || {};
    const groupDiscounts = config['Group Discounts'] || {};
    const paymentFrequencySurcharge = config['Payment Frequency Surcharge'] || {};

    // Outpatient/Client Type option labels come from the config's nested keys, so a rate
    // sheet update (e.g. a new outpatient tier) shows up here without a code change.
    const outpatientBands = Object.keys(outpatientRates);
    const outpatientBenefitItems = ['No', ...(outpatientBands.length > 0 ? Object.keys(outpatientRates[outpatientBands[0]] || {}) : ['Standard', 'Executive'])];

    const dentalBands = Object.keys(dentalRates);
    const clientTypeItems = dentalBands.length > 0 ? Object.keys(dentalRates[dentalBands[0]] || {}) : ['Individual', 'Group'];

    const coPaymentItems = Object.keys(coPaymentRates).length > 0 ? Object.keys(coPaymentRates) : ['Nil'];

    const personalAccidentCoverageItems = ['No', ...Object.keys(personalAccidentCoverageRates)];

    // The rate provider only quotes Annually - every other frequency is derived on the
    // frontend from the surcharge the rate sheet's config carries.
    const otherPaymentFrequencies = Object.keys(paymentFrequencySurcharge).filter(f => f !== 'Annually');
    const paymentFrequencyItems = ['Annually', ...(otherPaymentFrequencies.length > 0 ? otherPaymentFrequencies : ['Semi Annually'])];

    const [selectedTier, setSelectedTier] = useState('pacific_cross_select_plus');
    const [roomType, setRoomType] = useState('Ward');
    const [clientType, setClientType] = useState(clientTypeItems[0] || 'Individual');
    const [coPayment, setCoPayment] = useState(coPaymentItems[0] || 'Nil');
    const [outpatientBenefit, setOutpatientBenefit] = useState('No');
    const [dentalBenefit, setDentalBenefit] = useState('No');
    const [personalAccidentCoverage, setPersonalAccidentCoverage] = useState('No');
    const [paymentFrequency, setPaymentFrequency] = useState('Annually');
    const [discount, setDiscount] = useState('0');
    const [loadingPercent, setLoadingPercent] = useState('0');
    const [ratesData, setRatesData] = useState(null);
    // Set once View Rates fetched new rates, which replace any premiums edited by hand.
    const fetchedFreshRatesRef = useRef(false);
    // Rates were shown but an option that changes the fetch was edited since
    const [ratesStale, setRatesStale] = useState(false);
    // { [quoteUid]: boolean } — quotes whose premiums are left blank
    const [blankQuotes, setBlankQuotes] = useState({});
    const [loading, setLoading] = useState(false);
    const ratesEndRef = useRef(null);

    // Clear rates that no longer match the selected options, offering a refresh if any were shown
    function invalidateRates() {
        if (ratesData) setRatesStale(true);
        setRatesData(null);
    }

    useEffect(() => {
        if (ratesData) {
            ratesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [ratesData]);

    const selectedTierObj = TIERS.find(t => t.id === selectedTier) || TIERS[0];

    // Group Discounts only make sense for the Group client type - Individual quotes never
    // get a volume discount no matter how many clients are on it.
    function getGroupDiscountRate() {
        if (clientType !== 'Group') return 0;

        const count = clients.length;

        if (count === 0) return 0;

        let rate = 0;

        Object.keys(groupDiscounts).forEach(bracket => {
            if (bracket.endsWith('+')) {
                const min = parseInt(bracket, 10);
                if (!isNaN(min) && count >= min) rate = groupDiscounts[bracket];
            } else if (bracket.includes('-')) {
                const [min, max] = bracket.split('-').map(part => parseInt(part.trim(), 10));
                if (count >= min && count <= max) rate = groupDiscounts[bracket];
            } else if (count === parseInt(bracket, 10)) {
                rate = groupDiscounts[bracket];
            }
        });

        return rate;
    }

    // Pre-populate form when opening
    useEffect(() => {
        // A stale plan (a client or quote added since it was quoted) opens with the refresh offer showing.
        setRatesStale(open && !!editingPlan && isStale);
        fetchedFreshRatesRef.current = false;
        if (!open) {
            setRatesData(null);
            return;
        }
        if (open && editingPlan) {
            const savedTierObj = TIERS.find(t => t.id === editingPlan.plan_id) || TIERS[0];
            const savedRoomType = editingPlan.payloads?.room_type || 'Ward';
            setSelectedTier(savedTierObj.id);
            setRoomType(savedRoomType);
            setClientType(editingPlan.payloads?.client_type || clientTypeItems[0] || 'Individual');
            setCoPayment(editingPlan.payloads?.co_payment || coPaymentItems[0] || 'Nil');
            setOutpatientBenefit(editingPlan.payloads?.outpatient_benefit || 'No');
            setDentalBenefit(editingPlan.payloads?.dental_benefit || 'No');
            setPersonalAccidentCoverage(editingPlan.payloads?.personal_accident_coverage || 'No');
            setPaymentFrequency(editingPlan.payloads?.payment_frequency || 'Annually');
            setDiscount(String(editingPlan.payloads?.discount ?? '0'));
            setLoadingPercent(String(editingPlan.payloads?.loading ?? '0'));

            // Reconstruct ratesData from stored per-field premiums (saved before any discount)
            // — only the saved tier / room type's column is recoverable
            const ratesMap = {};
            (quotes || []).forEach(quote => {
                const quotation = (quotations || []).find(q => q.uid === quote.uid);
                if (!quotation) return;
                if (isQuoteBlankForPlan(quotation, editingPlan)) {
                    ratesMap[quote.uid] = BLANK_QUOTE_RATES;
                    return;
                }
                // Match the specific plan instance (plan_uid) so editing one of several same-tier
                // plans doesn't pull rates from a sibling; fall back to plan_id for plans saved
                // before per-instance uids existed.
                const rawRates = clients.map(client => {
                    const clientPremium = quotation.client_premiums?.find(cp => cp.client_uid === client.uid);
                    const premium = clientPremium?.premiums?.find(p =>
                        editingPlan.uid ? p.plan_uid === editingPlan.uid : p.plan_id === editingPlan.plan_id
                    );
                    return {
                        [getRateField(savedTierObj.key, savedRoomType)]: premium?.tier_premium ?? null,
                        op_standard: premium?.op_standard_premium ?? null,
                        op_executive: premium?.op_executive_premium ?? null,
                        dental: premium?.dental_premium ?? null,
                        personal_accident_coverage_rate: premium?.personal_accident_coverage_premium ?? null,
                    };
                });
                ratesMap[quote.uid] = { rawRates };
            });
            setRatesData(Object.keys(ratesMap).length > 0 ? ratesMap : null);
        } else if (open && !editingPlan) {
            setSelectedTier('pacific_cross_select_plus');
            setRoomType('Ward');
            setClientType(clientTypeItems[0] || 'Individual');
            setCoPayment(coPaymentItems[0] || 'Nil');
            setOutpatientBenefit('No');
            setDentalBenefit('No');
            setPersonalAccidentCoverage('No');
            setPaymentFrequency('Annually');
            setDiscount('0');
            setLoadingPercent('0');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        if (open) setBlankQuotes(getBlankQuotesForPlan(quotes, quotations, editingPlan));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    // Every tier × room type column comes back in one fetch, so switching just shows a
    // different column. Select Standard doesn't offer Private 3M/5M - fall back to Ward.
    function handleTierChange(tierId) {
        const nextTier = TIERS.find(t => t.id === tierId) || TIERS[0];
        setSelectedTier(nextTier.id);
        if (!nextTier.roomTypes.includes(roomType)) setRoomType(nextTier.roomTypes[0]);
    }

    // client_type, co_payment and personal_accident_coverage feed the rate provider
    // directly, so changing them needs a re-fetch. Room type / outpatient / dental only
    // pick which already-fetched value is shown, so they don't.
    function handleFetchedOptionChange(setter, value) {
        setter(value);
        invalidateRates();
    }

    // Quotes set to "Leave Blank" skip rating and render as empty columns in the preview
    function isBlankQuote(quote) {
        return !!blankQuotes[quote.uid];
    }

    async function handleViewRates() {
        setLoading(true);
        setRatesData(null);
        setRatesStale(false);

        try {
            const results = await Promise.all(
                (quotes || []).map(quote => isBlankQuote(quote)
                    ? Promise.resolve({ quoteUid: quote.uid, quote, blank: true })
                    : getRates({
                        token: auth.token,
                        body: {
                            clients,
                            // This insurer has no deductible concept - an empty placeholder
                            // is sent because the endpoint requires the `deductibles` key.
                            deductibles: { [INSURER_ID]: {} },
                            insurers: [INSURER_ID],
                            specifics: {
                                [INSURER_ID]: {
                                    client_type: clientType,
                                    co_payment: coPayment,
                                    personal_accident_coverage: personalAccidentCoverage,
                                },
                            },
                            specific_area_of_coverage: { [INSURER_ID]: 'Philippines' },
                            rates_version_per_insurer: { [INSURER_ID]: selectedRatesVersion?.[INSURER_ID] || '' },
                            specific_currency: { [INSURER_ID]: 'PHP' },
                            specific_payment_frequency: { [INSURER_ID]: 'Annually' },
                        },
                    }).then(response => ({ quoteUid: quote.uid, data: response?.data?.[INSURER_ID] }))
                )
            );

            const ratesMap = {};
            results.forEach(({ quoteUid, data, blank }) => {
                if (blank) {
                    ratesMap[quoteUid] = BLANK_QUOTE_RATES;
                    return;
                }
                if (data?.rates) {
                    const rawRates = data.rates.map(r => Object.fromEntries(RATE_FIELDS.map(f => [f, r[f]])));
                    ratesMap[quoteUid] = { rawRates };
                } else {
                    ratesMap[quoteUid] = data;
                }
            });
            setRatesData(ratesMap);
            fetchedFreshRatesRef.current = true;
        } catch (err) {
            console.error('Error fetching Pacific Cross Select rates:', err);
            toast.error('Error fetching rates', { hideProgressBar: true, autoClose: 3000 });
        }

        setLoading(false);
    }

    function getRawRate(clientIndex, key, quoteUid) {
        const raw = ratesData?.[quoteUid]?.rawRates?.[clientIndex];
        if (!raw) return null;
        const val = raw[key];
        return val === undefined ? null : val;
    }

    function getTierRate(clientIndex, quoteUid) {
        return getRawRate(clientIndex, getRateField(selectedTierObj.key, roomType), quoteUid);
    }

    // Group Discount + Renewal Discount/Loading combined into one multiplier, applied to
    // the tier rate and to Outpatient - Dental and Personal Accident Coverage stay flat.
    // Same as PacificCrossSelect_Rates' getDiscountFactor.
    function getDiscountFactor() {
        return (1 - getGroupDiscountRate()) * getRenewalPercent(discount, loadingPercent);
    }

    function getTierRateAfterDiscount(clientIndex, quoteUid) {
        const rate = getTierRate(clientIndex, quoteUid);
        if (!isRateAvailable(rate)) return rate;
        return rate * getDiscountFactor();
    }

    function getOptionalRate(clientIndex, benefit, quoteUid) {
        if (benefit === 'outpatient') {
            if (outpatientBenefit === 'No') return 0;
            return getRawRate(clientIndex, outpatientBenefit === 'Executive' ? 'op_executive' : 'op_standard', quoteUid);
        }
        if (benefit === 'dental') {
            if (dentalBenefit !== 'Yes') return 0;
            return getRawRate(clientIndex, 'dental', quoteUid);
        }
        if (benefit === 'personal_accident_coverage') {
            if (personalAccidentCoverage === 'No') return 0;
            return getRawRate(clientIndex, 'personal_accident_coverage_rate', quoteUid);
        }
        return 0;
    }

    function getDiscountedOptionalRate(clientIndex, benefit, quoteUid) {
        const rate = getOptionalRate(clientIndex, benefit, quoteUid);
        if (!isRateAvailable(rate)) return rate;
        if (benefit === 'outpatient') return rate * getDiscountFactor();
        return rate;
    }

    function getOptionalPremium(clientIndex, quoteUid) {
        let total = 0;
        ['outpatient', 'dental', 'personal_accident_coverage'].forEach(benefit => {
            const rate = getDiscountedOptionalRate(clientIndex, benefit, quoteUid);
            if (isRateAvailable(rate)) total += rate;
        });
        return total;
    }

    function getFinalPremium(clientIndex, quoteUid) {
        const tierRate = getTierRateAfterDiscount(clientIndex, quoteUid);
        if (!isRateAvailable(tierRate)) return tierRate;
        return tierRate + getOptionalPremium(clientIndex, quoteUid);
    }

    function getSubtotal(getRateForClient) {
        let total = 0;
        let hasAny = false;
        for (let i = 0; i < clients.length; i++) {
            const rate = getRateForClient(i);
            if (isRateAvailable(rate)) { total += rate; hasAny = true; }
        }
        return hasAny ? total : null;
    }

    // Annually is the only frequency the rate provider quotes - the rest are derived here
    // from the surcharge the rate sheet's config carries.
    function getPremiumPerPaymentFrequency(frequency, quoteUid, clientIndex = null) {
        const annual = clientIndex === null
            ? getSubtotal((idx) => getFinalPremium(idx, quoteUid))
            : getFinalPremium(clientIndex, quoteUid);
        if (!isRateAvailable(annual)) return annual;

        const surcharge = paymentFrequencySurcharge[frequency] || 0;
        if (frequency === 'Semi Annually') {
            return (annual + (annual * surcharge)) / 2;
        }
        const installments = { 'Annually': 1, 'Quarterly': 4, 'Monthly': 12 }[frequency] || 1;
        return (annual + (annual * surcharge)) / installments;
    }

    function getPaymentFrequencyLabel(frequency) {
        if (frequency === 'Annually') return 'Annual Premium';
        const percent = ((paymentFrequencySurcharge[frequency] || 0) * 100).toFixed(0);
        if (frequency === 'Semi Annually') return `Semi Annual Premium (${percent}%)`;
        return `${frequency} Premium (+${percent}%)`;
    }

    function buildPlanLabel() {
        return `${selectedTierObj.label} (${roomType})`;
    }

    const optionalRows = [
        { key: 'outpatient', label: `Outpatient (${outpatientBenefit})`, visible: outpatientBenefit !== 'No' },
        { key: 'dental', label: 'Dental', visible: dentalBenefit === 'Yes' },
        { key: 'personal_accident_coverage', label: 'Personal Accident Coverage', visible: personalAccidentCoverage !== 'No' },
    ].filter(row => row.visible);

    function handleAddToQuotations() {
        if (!ratesData || !onAddToQuotations) return;

        const planLabel = buildPlanLabel();
        const effectivePlanId = editingPlan ? editingPlan.plan_id : selectedTier;
        // plan_id identifies the tier and is shared by every instance of it; uid identifies this
        // specific plan instance so multiple plans of the same tier can coexist instead of
        // overwriting each other. Editing reuses the existing plan's uid.
        const planUid = editingPlan ? editingPlan.uid : uuidv4();

        const updates = [];
        (quotes || []).forEach(quote => {
            clients.forEach((client, clientIndex) => {
                const rate = getPremiumPerPaymentFrequency(paymentFrequency, quote.uid, clientIndex);

                updates.push({
                    quoteUid: quote.uid,
                    clientUid: client.uid,
                    planId: effectivePlanId,
                    planUid,
                    premium: {
                        insurer: INSURER_ID,
                        plan: selectedTierObj.key,
                        plan_label: planLabel,
                        included_benefits: '',
                        deductible: '',
                        area_of_coverage: 'Philippines',
                        room_type: roomType,
                        client_type: clientType,
                        co_payment: coPayment,
                        outpatient_benefit: outpatientBenefit,
                        dental_benefit: dentalBenefit,
                        personal_accident_coverage: personalAccidentCoverage,
                        currency: 'PHP',
                        payment_frequency: paymentFrequency,
                        discount,
                        loading: loadingPercent,
                        premium_rate: rate ?? null,
                        // Component premiums are the rate sheet values before any discount
                        tier_premium: getTierRate(clientIndex, quote.uid),
                        op_standard_premium: getRawRate(clientIndex, 'op_standard', quote.uid),
                        op_executive_premium: getRawRate(clientIndex, 'op_executive', quote.uid),
                        dental_premium: getRawRate(clientIndex, 'dental', quote.uid),
                        personal_accident_coverage_premium: getRawRate(clientIndex, 'personal_accident_coverage_rate', quote.uid),
                    },
                });
            });
        });

        const plan = {
            plan_id: effectivePlanId,
            uid: planUid,
            plan: selectedTierObj.key,
            insurer: INSURER_ID,
            label: `Pacific Cross Select ${planLabel}`,
            payloads: {
                room_type: roomType,
                client_type: clientType,
                co_payment: coPayment,
                outpatient_benefit: outpatientBenefit,
                dental_benefit: dentalBenefit,
                personal_accident_coverage: personalAccidentCoverage,
                payment_frequency: paymentFrequency,
                discount,
                loading: loadingPercent,
            },
        };

        onAddToQuotations(withFreshRatesFlag(applyLeaveBlank(updates, (quotes || []).filter(isBlankQuote).map(q => q.uid)), fetchedFreshRatesRef.current), plan);
        toast.success(editingPlan ? 'Premiums Updated' : 'Premiums Added', { hideProgressBar: true, autoClose: 3000 });
        onClose();
    }

    // Rates rebuilt in edit mode only hold the saved room type's column — switching Core
    // Benefits there leaves nothing to show until the rates are fetched again.
    const missingRateData = !!ratesData && (quotes || []).some(quote =>
        !isBlankQuoteRates(ratesData, quote.uid) && clients.some((client, idx) => getTierRate(idx, quote.uid) == null)
    );

    const groupDiscountRate = getGroupDiscountRate();
    const discountFactor = getDiscountFactor();
    const selectSx = { fontSize: 12, height: 32 };
    const menuItemSx = { fontSize: 12 };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 4,
                    maxHeight: '90vh',
                    backgroundColor: '#f1f4fa',
                },
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: BRAND_COLOR, px: 2, py: 1 }}>
                <div style={{
                    color: '#fff',
                    backgroundColor: BRAND_COLOR,
                    padding: 6,
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: 18,
                    lineHeight: 1.2,
                    height: 60,
                }}>Pacific Cross Select</div>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', background: '#f8f9fa', px: 4, overflow: 'hidden' }}>
                        <img
                            src={`${process.env.PUBLIC_URL}/images/pacific-cross-logo.avif`}
                            alt="Pacific Cross"
                            style={{ height: 60, userSelect: 'none', WebkitUserDrag: 'none', transform: 'scale(1.5)' }}
                            draggable={false}
                            onContextMenu={(e) => e.preventDefault()}
                        />
                    </Box>
                    <IconButton size="small" onClick={onClose} sx={{ color: '#fff', ml: 1 }}>
                        <CloseIcon fontSize="medium" />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent>
                <ManualEditNotice quotations={quotations} editingPlan={editingPlan} />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, rowGap: 2, mt: 3 }}>
                {/* Plan Type / Core Benefits / Client Type — same as PacificCrossSelect_Rates */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                    <Box>
                        <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Plan Type</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={selectedTier}
                                disabled={loading || !!editingPlan}
                                // Every tier's rate comes back in one fetch, so switching just shows a different column
                                onChange={(e) => handleTierChange(e.target.value)}
                                sx={selectSx}
                            >
                                {TIERS.map(tier => (
                                    <MenuItem key={tier.id} value={tier.id} sx={menuItemSx}>{tier.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    <Box>
                        <Typography fontWeight={600} sx={{ mb: 1, fontSize: 15 }}>Core Benefits</Typography>
                        <CustomSelect2
                            dense
                            sx={{ width: 170 }}
                            value={roomType}
                            onChange={(e) => setRoomType(e.target.value)}
                            items={selectedTierObj.roomTypes}
                            disabled={loading}
                            isZeroDeductibleUniform={false}
                        />
                    </Box>

                    <Box>
                        <Typography fontWeight={600} sx={{ fontSize: 15 }}>Client Type</Typography>
                        {clientTypeItems.map(item => (
                            <FormControlLabel
                                key={item}
                                sx={{ mb: 0, display: 'block', '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={clientType === item}
                                        onChange={() => { if (clientType !== item) handleFetchedOptionChange(setClientType, item); }}
                                        disabled={loading}
                                    />
                                }
                                label={item}
                            />
                        ))}
                        {groupDiscountRate > 0 && (
                            <Typography fontSize={13}>
                                Group Discount: <span style={{ color: BRAND_COLOR, fontWeight: 600 }}>{(groupDiscountRate * 100).toFixed(0)}%</span>
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Options — label on the left, select on the right */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 170px', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Co-Payment</Typography>
                        <CustomSelect2
                            dense
                            sx={{ width: 170 }}
                            value={coPayment}
                            onChange={(e) => handleFetchedOptionChange(setCoPayment, e.target.value)}
                            items={coPaymentItems}
                            disabled={loading}
                            isZeroDeductibleUniform={false}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Outpatient Benefits</Typography>
                        <CustomSelect2
                            dense
                            sx={{ width: 170 }}
                            value={outpatientBenefit}
                            onChange={(e) => setOutpatientBenefit(e.target.value)}
                            items={outpatientBenefitItems}
                            disabled={loading}
                            isZeroDeductibleUniform={false}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Dental Benefits</Typography>
                        <CustomSelect2
                            dense
                            sx={{ width: 170 }}
                            value={dentalBenefit}
                            onChange={(e) => setDentalBenefit(e.target.value)}
                            items={['No', 'Yes']}
                            disabled={loading}
                            isZeroDeductibleUniform={false}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Personal Accident Coverage</Typography>
                        <CustomSelect2
                            dense
                            sx={{ width: 170 }}
                            value={personalAccidentCoverage}
                            onChange={(e) => handleFetchedOptionChange(setPersonalAccidentCoverage, e.target.value)}
                            items={personalAccidentCoverageItems}
                            disabled={loading}
                            isZeroDeductibleUniform={false}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Payment Frequency</Typography>
                        <FormControl size="small" sx={{ width: 170 }}>
                            <Select
                                value={paymentFrequency}
                                disabled={loading}
                                // Derived from the annual premium on the frontend — no refetch
                                onChange={(e) => setPaymentFrequency(e.target.value)}
                                sx={selectSx}
                            >
                                {paymentFrequencyItems.map(frequency => (
                                    <MenuItem key={frequency} value={frequency} sx={menuItemSx}>{frequency}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                </Box>

                {/* Renewal/Further Discount & Loading */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto auto', columnGap: 3, rowGap: 1.5, alignItems: 'center', alignContent: 'start', mb: 2 }}>
                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Discount</Typography>
                        <PercentInput
                            value={discount}
                            onCommit={setDiscount}
                            disabled={loading}
                        />

                        <Typography fontWeight={600} sx={{ fontSize: 13 }}>Renewal/Further Loading</Typography>
                        <PercentInput
                            value={loadingPercent}
                            onCommit={setLoadingPercent}
                            disabled={loading}
                        />
                </Box>
                </Box>

                <Divider sx={{ mb: 2 }} />

                <LeaveBlankPerQuote
                    quotes={quotes}
                    blankQuotes={blankQuotes}
                    onChange={(next) => { setBlankQuotes(next); invalidateRates(); }}
                    disabled={loading}
                />

                {/* View Rates */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                        variant="contained"
                        onClick={handleViewRates}
                        disabled={loading || clients.length === 0}
                        size="small"
                        sx={{ backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                        endIcon={<ManageSearchIcon />}
                    >
                        View Rates
                    </Button>
                </Box>

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: BRAND_COLOR }} />
                    </Box>
                )}

                {!loading && ratesData && (
                    <Box>
                        <Divider sx={{ mb: 2 }} />
                        {missingRateData && (
                            <Typography sx={{ mb: 1.5, fontSize: 13, color: '#f44336' }}>
                                The rates for this Core Benefits option weren't loaded yet — click View Rates to fetch them before saving.
                            </Typography>
                        )}
                        {(quotes || []).map(quote => (
                            <Box key={quote.name} sx={{ mb: 3 }}>
                                <Typography fontWeight={600} sx={{ mb: 1 }}>
                                    {quote.name}
                                    <Typography component="span" sx={{ ml: 1, fontWeight: 400, color: 'text.secondary', fontSize: 13 }}>
                                        — {buildPlanLabel()}
                                    </Typography>
                                </Typography>
                                {isBlankQuoteRates(ratesData, quote.uid) ? <BlankQuoteNotice /> : (
<Paper variant="outlined" sx={{ overflowX: 'auto', backgroundColor: '#fff' }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ backgroundColor: BRAND_COLOR }}>
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 260 }}>Plan</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx} sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>
                                                        <div>{client.full_name || `Client ${idx + 1}`}{client.age ? ` (${client.age})` : ''}</div>
                                                        <div style={{ fontWeight: 400, fontSize: 12 }}>{client.country_of_residence || ''}</div>
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ color: '#fff', fontWeight: 600, minWidth: 100 }}>Subtotal</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {/* Tier premium, before any discount */}
                                            <TableRow sx={{ backgroundColor: '#f5f7fa' }}>
                                                <TableCell sx={{ color: 'text.secondary' }}>{buildPlanLabel()}</TableCell>
                                                {clients.map((client, idx) => (
                                                    <TableCell key={client.uid || idx}>
                                                        {formatRate(getTierRate(idx, quote.uid))}
                                                    </TableCell>
                                                ))}
                                                <TableCell sx={{ fontWeight: 500 }}>
                                                    {formatRate(getSubtotal((idx) => getTierRate(idx, quote.uid)))}
                                                </TableCell>
                                            </TableRow>

                                            {/* Tier premium after Group Discount / Renewal Discount & Loading */}
                                            {discountFactor !== 1 && (
                                                <TableRow sx={{ backgroundColor: PREMIUM_ROW_COLOR }}>
                                                    <TableCell sx={{ fontWeight: 600 }}>Premium after discount</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getTierRateAfterDiscount(idx, quote.uid))}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(getSubtotal((idx) => getTierRateAfterDiscount(idx, quote.uid)))}
                                                    </TableCell>
                                                </TableRow>
                                            )}

                                            {/* Optional benefits - Outpatient rides the discount, Dental and
                                                Personal Accident Coverage are flat add-ons */}
                                            {optionalRows.length > 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={clients.length + 2} sx={{ fontWeight: 600, background: BRAND_COLOR, color: '#fff' }}>Optional Premium</TableCell>
                                                </TableRow>
                                            )}
                                            {optionalRows.map((row, rowIdx) => (
                                                <TableRow key={row.key} sx={{ backgroundColor: rowIdx % 2 === 0 ? '#f5f7fa' : '#fff' }}>
                                                    <TableCell sx={{ color: 'text.secondary' }}>{row.label}</TableCell>
                                                    {clients.map((client, idx) => (
                                                        <TableCell key={client.uid || idx}>
                                                            {formatRate(getDiscountedOptionalRate(idx, row.key, quote.uid))}
                                                        </TableCell>
                                                    ))}
                                                    <TableCell sx={{ fontWeight: 500 }}>
                                                        {formatRate(getSubtotal((idx) => getDiscountedOptionalRate(idx, row.key, quote.uid)))}
                                                    </TableCell>
                                                </TableRow>
                                            ))}

                                            {/* Premium per payment frequency — same rows as PacificCrossSelect_Rates;
                                                the selected Payment Frequency is the one saved */}
                                            {paymentFrequencyItems.map(frequency => {
                                                const isSelected = frequency === paymentFrequency;
                                                return (
                                                    <TableRow key={frequency} sx={{ backgroundColor: isSelected ? SELECTED_PREMIUM_ROW_COLOR : PREMIUM_ROW_COLOR }}>
                                                        <TableCell sx={{ fontWeight: isSelected ? 700 : 600 }}>{getPaymentFrequencyLabel(frequency)}</TableCell>
                                                        {clients.map((client, idx) => (
                                                            <TableCell key={client.uid || idx} sx={{ fontWeight: isSelected ? 700 : 400 }}>
                                                                {formatRate(getPremiumPerPaymentFrequency(frequency, quote.uid, idx))}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell sx={{ fontWeight: isSelected ? 700 : 500 }}>
                                                            {formatRate(getPremiumPerPaymentFrequency(frequency, quote.uid))}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </Paper>
)}
                            </Box>
                        ))}
                        <div ref={ratesEndRef} />
                    </Box>
                )}

                <NotesDisplay appConfig={appConfig} insurerId={INSURER_ID} />

                <RatesVersionSelect
                    insurer={INSURER_ID}
                    label="Rates Version"
                    insurerRates={insurerRates}
                    selectedRatesVersion={selectedRatesVersion}
                    setSelectedRatesVersion={setSelectedRatesVersion}
                    disabled={loading}
                    onChange={invalidateRates}
                    handleFetchAppConfig={handleFetchAppConfig}
                    onRatesVersionChanged={onRatesVersionChanged}
                />

                {/* Same "Click to Refresh" as Quotation1, pinned to the bottom of the scroll area */}
                <Box sx={{ position: 'sticky', bottom: 16, height: 0, zIndex: 2 }}>
                    <Box sx={{ position: 'absolute', bottom: 0, left: '50%', transform: 'translateX(-50%)' }}>
                        <Zoom in={ratesStale && !loading} unmountOnExit>
                            <Fab
                                onClick={handleViewRates}
                                style={{
                                    backgroundColor: 'rgba(187, 222, 251, 0.55)',
                                    backdropFilter: 'blur(10px)',
                                    WebkitBackdropFilter: 'blur(10px)',
                                    border: '1px solid rgba(255, 255, 255, 0.3)',
                                    boxShadow: '0 4px 30px rgba(0, 0, 0, 0.15)',
                                    whiteSpace: 'nowrap',
                                }}
                                color='default'
                                variant="extended">
                                Click to Refresh
                                <RefreshIcon sx={{ ml: 1 }} />
                            </Fab>
                        </Zoom>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={handleAddToQuotations}
                    disabled={!ratesData || loading || missingRateData}
                    sx={{ display: ratesData ? 'inline-flex' : 'none', backgroundColor: '#673ab7', textTransform: 'none', '&:hover': { backgroundColor: '#512da8' }, minWidth: 120 }}
                    endIcon={editingPlan ? <SyncIcon /> : <AddIcon />}
                >{editingPlan ? 'Update Premiums' : 'Add Premiums'}</Button>
            </DialogActions>
        </Dialog>
    );
}
